import { secondaryNavObj } from '@/constants/secondaryNav';
import { RoutesList } from '@/routes/routesList';
import { Category } from '@/types/category';
import { UniqueIdentifier } from '@dnd-kit/core';
import { formatDuration, intervalToDuration } from 'date-fns';
import { Location } from 'react-router-dom';

export { default as sleep } from './sleep';

const shortFormatDistanceLocale = {
  xSeconds: '{{count}}s',
  xMinutes: '{{count}}m',
  xHours: '{{count}}h',
  xDays: '{{count}}d',
  xWeeks: '{{count}}w',
  xMonths: '{{count}}mo',
  xYears: '{{count}}y',
};

const shortEnLocale = {
  formatDistance: (token: keyof typeof shortFormatDistanceLocale, count: string) => {
    return shortFormatDistanceLocale[token].replace('{{count}}', count);
  },
};

export const getCurrentOverCategory = (categories?: Category[], overContainer?: UniqueIdentifier): Category | null => {
  return categories?.find((el: Category) => el.name === overContainer) ?? null;
};

export const checkIfOnlySpaces = (str?: string) => {
  return str?.trim().length === 0;
};

// Runs per card per render and intervalToDuration is expensive (several calendar
// difference calculations), while duration values repeat constantly — memoized for the
// default locale. Keyed on Intl.DateTimeFormat identity so mocked-timezone tests stay
// correct (mockTimezone swaps that constructor alongside the Date offset mock).
const humanDurationCache = new Map<number, string>();
let humanDurationCacheCtor: unknown;

export function humanDuration(seconds: number, locale = shortEnLocale) {
  if (locale !== shortEnLocale) {
    return computeHumanDuration(seconds, locale);
  }
  const ctor = Intl.DateTimeFormat;
  if (humanDurationCacheCtor !== ctor) {
    humanDurationCache.clear();
    humanDurationCacheCtor = ctor;
  }
  let cached = humanDurationCache.get(seconds);
  if (cached === undefined) {
    if (humanDurationCache.size > 10000) humanDurationCache.clear();
    cached = computeHumanDuration(seconds, locale);
    humanDurationCache.set(seconds, cached);
  }
  return cached;
}

function computeHumanDuration(seconds: number, locale: typeof shortEnLocale) {
  return formatDuration(intervalToDuration({ start: 0, end: seconds * 1000 }), {
    format: ['years', 'months', 'weeks', 'days', 'hours', 'minutes', 'seconds'],
    locale,
  });
}

/**
 * @method
 * Gets the number of seconds from a duration in the format of HH:MM:SS
 */
export function getSecondsFromDuration(duration?: string | null) {
  const [hours, minutes, seconds] = (duration || '00:00:00').split(':').map((n) => +n);

  return hours * 60 * 60 + (minutes || 0) * 60 + (seconds || 0);
}

export function isPlanningPage(location: Location) {
  return (
    location.pathname.includes(RoutesList.MyPlanPage) ||
    location.pathname.includes(RoutesList.MyPlanDailyPage) ||
    location.pathname.includes(RoutesList.SchedulePage) ||
    location.pathname.includes(RoutesList.CelebratePage)
  );
}

export function isPlanSchedulePage(location: Location) {
  return (
    location.pathname.includes(RoutesList.MyPlanPage) ||
    location.pathname.includes(RoutesList.MyPlanDailyPage) ||
    location.pathname.includes(RoutesList.SchedulePage)
  );
}

export function convertDurationToMinutes(duration?: string | null): number {
  const [hours, minutes, seconds] = (duration || '00:00:00').split(':').map(Number);
  return hours * 60 + (minutes || 0) + (seconds || 0) / 60;
}

export function isCurrentPageActive(pagePath: string): boolean {
  // Get the current path of the page.
  const currentPath = window.location.pathname;

  // Check if the current path matches the given page path.
  return currentPath === pagePath;
}
export function isInCategoryManagerPage() {
  const pathname = window.location.pathname;

  return pathname.includes(RoutesList.CategoryManagerPage);
}

export const isOnProjectDetailPage = () => window.location.pathname.includes(RoutesList.ProjectDetailPage);

export function getCategoryIdFromUrl(urlString = window.location.href) {
  // Create a URL object
  const url = new URL(urlString);

  // Access the pathname and split it to get the last part
  const matches = url.toString().match(/\/[\w-]+\/([0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12})/);

  if (matches) {
    const UUID = matches[1];

    return UUID;
  } else {
    return null;
  }
}

export function isUncategorized(category: Category | undefined) {
  if (!category) {
    return;
  }

  return category?.name?.toLowerCase() === 'uncategorized';
}

// Centralized translations for "Capture" category
export const getCaptureTranslations = (): Record<string, string> => {
  return {
    en: 'Capture',
    es: 'Captura',
    // Add more languages as needed
    // fr: 'Capturer',
    // de: 'Erfassen',
  };
};

// Get all possible capture translations in lowercase for validation
export const getReservedCategoryNames = (): string[] => {
  const captureTranslations = getCaptureTranslations();
  return ['uncategorized', 'uncategorised', ...Object.values(captureTranslations).map((name) => name.toLowerCase())];
};

export function fixUncategorizedName(categoryName: string) {
  // Client wanted Uncategorized to be renamed to Capture very last minute,
  // and we simply couldn't change backend too and deal with all the
  // implications. This code renames the surface labels appearing in the app
  // without changing the underliying name of Uncategorized and touching any
  // logic attached to it.
  // TODO: if the change is confirmed, propagate to backend too
  if (categoryName?.toLowerCase() === 'uncategorized') {
    // Get current language from localStorage
    const currentLanguage = localStorage.getItem('i18nextLng') || 'en';

    const translations = getCaptureTranslations();

    return translations[currentLanguage] || translations.en;
  }

  return categoryName;
}

export function showSecondaryNavItemsBasedOnLocation(location: Location) {
  return secondaryNavObj.find((item) => item.href === location.pathname);
}

export const capitalizeFirstLetter = (text: string) => {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};
